import { Injectable, Type } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import clone from 'clone';
import { of } from 'rxjs';
import { concatMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { InsightsUserHomeShellComponent } from 'src/app/home/insights-user-home/insights-user-home-shell.component';
import { SnackService } from 'src/app/services/snack.service';
import { HomeActions } from 'src/app/state/home';
import { InsightsLibraryActions, InsightsLibrarySelectors } from '.';
import { DashboardPdfExportData, DashboardPdfExportDialog } from '../../../dashboards/dialogs/pdf-export/pdf-export.dialog';
import { DashboardsActions } from '../../../dashboards/state/dashboards';
import { getInsightsPbiChartType } from '../../../shared/chart/chart';
import { DashboardService, DashboardType, ListUserDashboardDto, ListUserVisualDto, PdfPaperSize, UserDashboardService, UserVisualService, VisualService } from '../../../services/api.services';
import { catchAppError } from '../../../shared/utils';
import { AppActions, AppSelectors } from '../../../state/app';
import { InsightsLibraryCopiedSnack, InsightsLibraryCopiedSnackData } from '../../components/snacks/copied.snack';
import { InsightsLibraryToggleHomeSnack, InsightsLibraryToggleHomeSnackData, InsightsLibraryToggleHomeSnackDataAction } from '../../components/snacks/toggle-home.snack';
import { InsightsLibraryShellComponent } from '../../containers/insights-library-shell/insights-library-shell.component';
import { InsightsLibraryDashboard, InsightsLibraryDashboardCategory, InsightsLibraryVisual, InsightsLibraryVisualCategory } from './types';
import { createItemsAndCategories, getSnackConfig } from './utils';
import { VisualsActions } from 'src/app/pivot-studio/state/visuals';

@Injectable()
export class InsightsLibraryEffects {
  private dialogRef: MatDialogRef<InsightsLibraryShellComponent>;

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private snackService: SnackService,
    private dialog: MatDialog,
    private dashboardService: DashboardService,
    private visualService: VisualService,
    private userDashboardService: UserDashboardService,
    private userVisualService: UserVisualService) {

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      tap(() => this.store.dispatch(InsightsLibraryActions.setVisible({ visible: false })))
    ).subscribe();
  }


  toggleDialog$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.setVisible),
    tap(({ visible }) => {
      if (visible) {
        this.dialogRef = this.dialog.open(InsightsLibraryShellComponent, InsightsLibraryShellComponent.dialogConfig);

        this.dialogRef.afterClosed().subscribe(() => {
          this.store.dispatch(InsightsLibraryActions.setVisible({ visible: false }));
        });
      }
      else {
        if (this.dialogRef) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      }
    })),
    { dispatch: false }
  );



  loadDashboards$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.loadDashboards),
    switchMap(() => this.dashboardService.listDashboards(true).pipe(
      map((dashboards) => InsightsLibraryActions.loadDashboardsSuccess({ dashboards: dashboards.filter(d => d.fromLibrary) })),
      catchAppError(error => {
        return of(InsightsLibraryActions.loadDashboardsFailure({ error }));
      })
    )))
  );

  loadVisuals$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.loadVisuals),
    switchMap(() => this.visualService.listVisuals({
        includeLibrary: true,
        includeSamples: false,
        includeShared: false,
        includeContent: true
      }).pipe(
      map((visuals) => InsightsLibraryActions.loadVisualsSuccess({ visuals: visuals.filter(v => v.fromLibrary) })),
      catchAppError(error => {
        return of(InsightsLibraryActions.loadVisualsFailure({ error }));
      })
    )))
  );


  setDashboardCategories$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.loadDashboardsSuccess),
    withLatestFrom(
      this.store.select(AppSelectors.getItemCategories),
      this.store.select(InsightsLibrarySelectors.dashboards),
    ),
    map(([{ }, itemCategories, dashboards]) => {
      let { items, categories } = createItemsAndCategories<InsightsLibraryDashboard, InsightsLibraryDashboardCategory, ListUserDashboardDto>(
        itemCategories,
        dashboards,
        d => ({
          name: d.name,
          favourite: d.favourite,

          dashboard: d,

          canView: true,
          canCopy: d.fromLibrary && d.type === DashboardType.Insights,
          canAddToHome: true,

          fields: [],
          initialFilters: clone(d.filters),
          filters: d.filters
        }));

      return InsightsLibraryActions.dashboardItemSelector.setItemsAndCategories({ items, categories });
    })
  ));

  setVisualCategories$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.loadVisualsSuccess),
    withLatestFrom(
      this.store.select(AppSelectors.getItemCategories),
      this.store.select(InsightsLibrarySelectors.visuals),
    ),
    map(([{ }, itemCategories, visuals]) => {
      let { items, categories } = createItemsAndCategories<InsightsLibraryVisual, InsightsLibraryVisualCategory, ListUserVisualDto>(
        itemCategories,
        visuals,
        v => ({
          name: v.name,
          favourite: v.favourite,

          visual: v,

          canView: true,
          canCopy: v.fromLibrary,

          chartType: getInsightsPbiChartType(v.type),
          fields: v.fields,
          initialFilters: clone(v.filters),
          filters: v.filters,

          subtitle: v.subtitle
        }));

      return InsightsLibraryActions.visualItemSelector.setItemsAndCategories({ items, categories });
    })
  ));


  setInitialDashboardCategory$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.dashboardItemSelector.setItemsAndCategories),
    map(() => InsightsLibraryActions.dashboardItemSelector.setInitialCategory()))
  );

  setInitialVisualCategory$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.visualItemSelector.setItemsAndCategories),
    map(() => InsightsLibraryActions.visualItemSelector.setInitialCategory()))
  );


  setStarredOnly$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.setStarredOnly),
    concatMap(({ starredOnly }) => [
      InsightsLibraryActions.dashboardItemSelector.setStarredOnly({ starredOnly }),
      InsightsLibraryActions.visualItemSelector.setStarredOnly({ starredOnly })
    ])
  ));

  setFilter$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.setFilter),
    concatMap(({ filter }) => [
      InsightsLibraryActions.dashboardItemSelector.setFilter({ filter }),
      InsightsLibraryActions.visualItemSelector.setFilter({ filter })
    ])
  ));


  updateDashboardsVisibility$ = createEffect(() => this.actions$.pipe(
    ofType(
      InsightsLibraryActions.loadDashboardsSuccess,
      InsightsLibraryActions.dashboardItemSelector.setStarredOnly,
      InsightsLibraryActions.dashboardItemSelector.setFilter,
      InsightsLibraryActions.dashboardItemSelector.updateItem,
    ),
    withLatestFrom(
      this.store.select(InsightsLibrarySelectors.loadingDashboards),
    ),
    filter(([action, loadingDashboards]) => !loadingDashboards),
    map(([action, loadingDashboards]) => {
      let starredOnlyChanged = true;
      let filterChanged = true;

      if (action.type === InsightsLibraryActions.dashboardItemSelector.setFilter.type) {
        // When updating the filter, we know starred state would not change, so skip calculating visibility from that.
        starredOnlyChanged = false;
      }
      else {
        // Otherwise, do not update the filter unless this is the load task.
        // On load, everything has to be calculated.
        if (action.type !== InsightsLibraryActions.loadDashboardsSuccess.type) {
          filterChanged = false;
        }
      }

      return InsightsLibraryActions.dashboardItemSelector.updateVisibility({ starredOnlyChanged, filterChanged });
    })
  ));

  updateVisualsVisibility$ = createEffect(() => this.actions$.pipe(
    ofType(
      InsightsLibraryActions.loadVisualsSuccess,
      InsightsLibraryActions.visualItemSelector.setStarredOnly,
      InsightsLibraryActions.visualItemSelector.setFilter,
      InsightsLibraryActions.visualItemSelector.updateItem,
    ),
    withLatestFrom(
      this.store.select(InsightsLibrarySelectors.loadingVisuals),
    ),
    filter(([action, loadingVisuals]) => !loadingVisuals),
    map(([action, loadingVisuals]) => {
      let starredOnlyChanged = true;
      let filterChanged = true;

      if (action.type === InsightsLibraryActions.visualItemSelector.setFilter.type) {
        // When updating the filter, we know starred state would not change, so skip calculating visibility from that.
        starredOnlyChanged = false;
      }
      else {
        // Otherwise, do not update the filter unless this is the load task.
        // On load, everything has to be calculated.
        if (action.type !== InsightsLibraryActions.loadVisualsSuccess.type) {
          filterChanged = false;
        }
      }

      return InsightsLibraryActions.visualItemSelector.updateVisibility({ starredOnlyChanged, filterChanged });
    })
  ));

  viewDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.viewDashboard),
    tap(() => {
      this.store.dispatch(InsightsLibraryActions.toggleViewMode({ viewMode: true }));
    })),
    { dispatch: false }
  );

  viewVisual$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.viewVisual),
    tap(() => {
      this.store.dispatch(InsightsLibraryActions.toggleViewMode({ viewMode: true }));
    })),
    { dispatch: false }
  );


  copyDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.copyDashboard),
    concatMap(({ item }) => {
      return this.dashboardService.copyDashboard(item.dashboard.id).pipe(
        map((dashboard) => InsightsLibraryActions.copyDashboardSuccess({ dashboard })),
        catchAppError(error => {
          return of(InsightsLibraryActions.copyDashboardFailure({ error }));
        })
      );
    }))
  );

  copyVisual$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.copyVisual),
    concatMap(({ item }) => {
      return this.visualService.copyVisual({ visualId: item.visual.id, copyContent: true }).pipe(
        map((visual) => InsightsLibraryActions.copyVisualSuccess({ visual })),
        catchAppError(error => {
          return of(InsightsLibraryActions.copyVisualFailure({ error }));
        })
      );
    }))
  );


  showSnackAfterCopyDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.copyDashboardSuccess),
    tap(({ dashboard }) => {
      this.snackService.openFromComponent(InsightsLibraryCopiedSnack,
        getSnackConfig(<InsightsLibraryCopiedSnackData>{
          itemName: dashboard.name,
          destination: 'My Dashboards',
          link: `/dashboards/${dashboard.id}/edit`
        }, 6000));
    })),
    { dispatch: false }
  );

  showSnackAfterCopyVisual$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.copyVisualSuccess),
    tap(({ visual }) => {
      this.snackService.openFromComponent(InsightsLibraryCopiedSnack,
        getSnackConfig(<InsightsLibraryCopiedSnackData>{
          itemName: visual.name,
          destination: 'My Visuals',
          link: `/visuals/${visual.id}`
        }, 6000));
    })),
    { dispatch: false }
  );

  updateUserDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.updateUserDashboard),
    concatMap(({ item, userDashboard }) => {
      return this.userDashboardService.updateUserDashboard(userDashboard).pipe(
        map(() => InsightsLibraryActions.updateUserDashboardSuccess({ item, userDashboard })),
        catchAppError(error => {
          return of(InsightsLibraryActions.updateUserDashboardFailure({ error }));
        })
      );
    }))
  );

  updateUserVisual$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.updateUserVisual),
    concatMap(({ item, userVisual }) => {
      return this.userVisualService.updateUserVisual(userVisual).pipe(
        map(() => InsightsLibraryActions.updateUserVisualSuccess({ item, userVisual })),
        catchAppError(error => {
          return of(InsightsLibraryActions.updateUserVisualFailure({ error }));
        })
      );
    }))
  );


  afterUserDashboardUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.updateUserDashboardSuccess),
    map(({ item, userDashboard }) => InsightsLibraryActions.dashboardItemSelector.updateItem({
      id: item.id,
      data: {
        dashboard: {
          ...item.dashboard,
          favourite: userDashboard.favourite,
          home: userDashboard.home
        },
        favourite: userDashboard.favourite,
      }
    })))
  );

  afterUserVisualUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.updateUserVisualSuccess),
    map(({ item, userVisual }) => InsightsLibraryActions.visualItemSelector.updateItem({
      id: item.id,
      data: {
        visual: {
          ...item.visual,
          favourite: userVisual.favourite,
          home: userVisual.home
        },
        favourite: userVisual.favourite,
        home: userVisual.home
      }
    })))
  );


  updateOtherDashboardStates$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.updateUserDashboardSuccess),
    tap(({ item, userDashboard }) => {
      if (item.dashboard.home !== userDashboard.home) {
        this.snackService.openFromComponent(InsightsLibraryToggleHomeSnack,
          getSnackConfig(<InsightsLibraryToggleHomeSnackData>{
            itemName: item.dashboard.name,
            destination: 'Home',
            action: userDashboard.home ? InsightsLibraryToggleHomeSnackDataAction.Added : InsightsLibraryToggleHomeSnackDataAction.Removed
          })
        );      
      } 

      let pageComponent = this.getPageComponentName();
      if (pageComponent === 'DashboardsShellComponent') {
        this.store.dispatch(DashboardsActions.updateUserDashboardSuccess({
          userDashboard: {
            dashboardId: item.dashboard.id,
            favourite: userDashboard.favourite,
            home: userDashboard.home
          }
        }));
      }
      else if (pageComponent === "HomeRootShellComponent") {
        this.store.dispatch(HomeActions.userDashboardOrVisualChanged());
      }
    })),
    { dispatch: false }
  );

  updateOtherVisualStates$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.updateUserVisualSuccess),
    tap(({ item, userVisual }) => {
      if (item.visual.home !== userVisual.home) {
        this.snackService.openFromComponent(InsightsLibraryToggleHomeSnack,
          getSnackConfig(<InsightsLibraryToggleHomeSnackData>{
            itemName: item.visual.name,
            destination: 'Home',
            action: userVisual.home ? InsightsLibraryToggleHomeSnackDataAction.Added : InsightsLibraryToggleHomeSnackDataAction.Removed
          })
        );      
      } 
      let pageComponent = this.getPageComponentName();
      if (pageComponent === 'VisualsShellComponent') {
        this.store.dispatch(VisualsActions.updateUserVisualSuccess({
          userVisual: {
            visualId: item.visual.id,
            favourite: userVisual.favourite,
            home: userVisual.home
          }
        }));
      }
      else if (pageComponent === "HomeRootShellComponent") {
        this.store.dispatch(HomeActions.userDashboardOrVisualChanged());
      }
    })),
    { dispatch: false }
  );

  exportDashboardToPdf$ = createEffect(() => this.actions$.pipe(
    ofType(InsightsLibraryActions.exportDashboardToPdf),
    withLatestFrom(
      this.store.select(AppSelectors.getUser),
      this.store.select(AppSelectors.getUserState),
    ),
    tap(([{ item }, user, userState]) => {
      this.dialog.open(DashboardPdfExportDialog, {
        width: '500px',
        minHeight: '440px',
        disableClose: true,
        data: <DashboardPdfExportData>{
          dashboard: item.dashboard,
          layoutOptions: user.roles.isAdmin,
          paperSize: userState?.defaultPdfPaperSize || PdfPaperSize.A4,
          filters: item.filters
        }
      });
    })),
    { dispatch: false }
  );


  getPageComponentName() {
    let pageComp = <Type<any>>this.activatedRoute.children[0]?.children[0]?.component
    return pageComp.name;
  }
}
